import { useEffect } from 'react';

import actions from '../../../../actions';
import api from '../../../../services/api';
import { storage } from '../../../../services/localstorage';
import store from '../../../../store';
import {
    decrypt,
    encrypt,
    formatFullDate,
    isFutureVisit
} from '../../../../utils';

export const FUTURE_VISITS_KEY = 'data_a';
export const PAST_VISITS_KEY = 'data_p';

export const FB_SYNC_KEY = 'docs';
export const FAVORITES_KEY = 'data_f';
export const GET_PAST_VISITS_FROM_FB = false;

export const decryptVisits = (visits = [], hashKey) => {
    const info = store.getState()?.user?.info;

    try {
        return visits?.map?.((visit) => decrypt(visit, info?.hashKey ?? hashKey ?? '')) ?? [];
    } catch (e) {
        return [];
    }
};

export const encryptVisits = (visits = [], hashKey) => {
    const info = store.getState()?.user?.info;

    try {
        return visits?.map?.((visit) => encrypt(visit, info?.hashKey ?? hashKey ?? '')) ?? [];
    } catch (e) {
        return [];
    }
};

function mergeArrays(primaryArray, secondaryArray, key = 'id') {
    const map = new Map();

    primaryArray.forEach((item) => {
        map.set(item?.[key], { ...item });
    });

    secondaryArray.forEach((item) => {
        if (map.has(item?.[key])) {
            map.set(item?.[key], {
                ...map.get(item?.[key]),
                ...item,
            });
        } else {
            map.set(item?.[key], { ...item });
        }
    });

    return Array.from(map.values());
}

export const updatePastVisits = async (pVisits) => {
    if (pVisits && pVisits?.length) {
        const currentPVisits = decryptVisits(await storage.get(PAST_VISITS_KEY));
        const result = (currentPVisits?.length ? mergeArrays(currentPVisits, pVisits) : pVisits)?.filter(
            (visit) => visit.status !== 'cancelled',
        );
        const enc = encryptVisits(result) ?? [];
        await storage.set(PAST_VISITS_KEY, enc);
        storage
            .get(FB_SYNC_KEY)
            .then((d) => {
                const allData = typeof d === 'object' && d !== null ? { ...d } : {};
                allData[store?.getState?.()?.user?.info?.me?.profileId] = enc;
                storage.set(FB_SYNC_KEY, allData)
                    .catch((err) => {
                        console.log(`error set ${FB_SYNC_KEY}`, { err });
                    });
            })
            .catch((e) => {
                console.log(`error get ${FB_SYNC_KEY}`, { e });
            });
        actions.setPurePastVisits(result);
    } else {
        actions.stopLoadingPastVisits();
    }
};

export const removePastVisit = async (visitId) => {
    const currentPVisits = decryptVisits(await storage.get(PAST_VISITS_KEY));
    const result = currentPVisits.filter((item) => item?.id !== visitId);

    const enc = encryptVisits(result) ?? [];
    await storage.set(PAST_VISITS_KEY, enc);
    storage
        .get(FB_SYNC_KEY)
        .then((allData = {}) => {
            allData[store?.getState?.()?.user?.info?.me?.profileId] = enc;
            storage.set(FB_SYNC_KEY, allData);
        })
        .catch();
    actions.setPurePastVisits(result);
};

export const removeFutureVisit = async (visitId) => {
    const currentFVisits = decryptVisits(await storage.get(FUTURE_VISITS_KEY)) ?? [];
    const result = currentFVisits.filter((item) => item?.id !== visitId);
    if (currentFVisits?.length === result?.length) {
        removePastVisit(visitId);
    } else {
        await storage.set(FUTURE_VISITS_KEY, encryptVisits(result));
    }
};

const setUpPastVisits = async () => {
    const currentPVisits = decryptVisits(await storage.get(PAST_VISITS_KEY)) ?? [];
    actions.setPurePastVisits(currentPVisits);
};

export const loadPastVisits = async (branchId, profileId) => {
    actions.startLoadingPastVisits();
    const visits = await api.getPastVisits('1990-01-01', formatFullDate(new Date()), null, branchId, profileId);
    updatePastVisits(visits);
};

function findPastVisits(newVisits, oldVisits, key = 'id') {
    const newVisitIds = new Set(newVisits.filter((visit) => visit?.status !== 'canceled')
        .map((visit) => visit?.[key]));

    return oldVisits.filter((visit) => visit?.status !== 'canceled' && !newVisitIds.has(visit?.[key]));
}

export const migrateFutureToPastVisits = async (newFVisits = []) => {
    const fVisits = decryptVisits(await storage.get(FUTURE_VISITS_KEY));
    if (fVisits && fVisits?.length) {
        const newPastVisits = findPastVisits(newFVisits, fVisits);
        if (newPastVisits && newPastVisits?.length) {
            updatePastVisits(newPastVisits);
        }
    }
    await storage.set(FUTURE_VISITS_KEY, encryptVisits(newFVisits));
};

export const useCheckForPastVisitsUpdates = (profileId, visits) => {
    useEffect(() => {
        if (!(visits && visits?.length)) {
            setUpPastVisits();
        }
    }, [ profileId ]);
};

export const clearVisits = async () => {
    await storage.set(FUTURE_VISITS_KEY, null);
    await storage.set(PAST_VISITS_KEY, null);
};

export const updateAuxiliaryData = async ({
    orderId,
    name,
    comment,
    drugs = [],
    attachments = [],
    providerCode,
    profileId,
    id,
}) => {
    const currentPVisits = decryptVisits(await storage.get(PAST_VISITS_KEY));
    const savedPVisit = currentPVisits?.find((e) => e.id === id);
    if (savedPVisit) {
        updatePastVisits([
            {
                ...savedPVisit,
                orderId,
                name,
                comment,
                drugs,
                attachments,
                providerCode,
                profileId,
                id,
            },
        ]);
    }
};

const isPastVisit = (order) => {
    return (
        !isFutureVisit(order?.endDate) ||
        (order?.status !== 'reserved' && order?.status !== 'created_manually' && order?.status !== 'cancelled')
    );
};

export const getOnlyPastVisits = (data = []) => {
    return data?.filter((visit) => visit.status !== 'cancelled')
        ?.filter((order) => isPastVisit(order));
};

const isPurePastVisit = (order) => {
    return !isFutureVisit(order?.endDate) || (order?.status !== 'reserved' && order?.status !== 'created_manually');
};

const getOnlyFutureVisits = (data) => {
    return data?.filter((visit) => visit.status !== 'cancelled')
        ?.filter((order) => !isPastVisit(order));
};

const getAllPastVisits = (data) => {
    return data?.filter((order) => isPurePastVisit(order));
};

export const updateAllVisits = (fullData) => {
    const allFutureVisits = getOnlyFutureVisits(fullData);
    const allPastVisits = getAllPastVisits(fullData);
    if (allPastVisits && allPastVisits?.length) {
        updatePastVisits(allPastVisits);
    }
    if (allFutureVisits && allFutureVisits?.length) {
        storage.set(FUTURE_VISITS_KEY, encryptVisits(allFutureVisits));
    }
};
